import type { ComponentProps } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'

import { Button } from '@/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/ui/dialog'
import { FormField } from '@/ui/form-field'

import { keepKeysOutOfMenu } from './data-grid-filter-shared'

type SavedViewNameForm = { name: string }

export function DataGridSavedViewNameDialog({
  open,
  title,
  description,
  initialName,
  submitLabel,
  pendingLabel,
  nameMaxLength,
  mutation,
  onSubmit,
  onClose,
  finalFocus,
}: {
  open: boolean
  title: string
  description: string
  initialName: string
  submitLabel: string
  pendingLabel: string
  nameMaxLength: number
  mutation: {
    isPending: boolean
    isError: boolean
    error: Error | null
    reset: () => void
    errorMessage: (error: unknown) => string
  }
  onSubmit: (name: string, onDone: () => void) => void
  onClose: () => void
  finalFocus: ComponentProps<typeof DialogContent>['finalFocus']
}) {
  const { control, handleSubmit, reset } = useForm<SavedViewNameForm>({
    resolver: zodResolver(z.object({
      name: z.string().trim().min(1, 'Enter a view name.').max(
        nameMaxLength,
        `View name must be ${nameMaxLength} characters or fewer.`,
      ),
    })),
    values: { name: initialName },
    mode: 'onSubmit',
  })
  const name = useWatch({ control, name: 'name' })

  function close() {
    onClose()
    reset()
    mutation.reset()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !mutation.isPending) close()
      }}
    >
      <DialogContent finalFocus={finalFocus}>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
        <form
          className="flex flex-col gap-3"
          onChange={() => {
            if (mutation.isError) mutation.reset()
          }}
          onSubmit={(event) => {
            event.preventDefault()
            void handleSubmit((values) => onSubmit(values.name.trim(), close))(event)
          }}
        >
          <FormField
            control={control}
            name="name"
            spec={{ label: 'View name' }}
            maxLength={nameMaxLength}
            disabled={mutation.isPending}
            onKeyDown={keepKeysOutOfMenu}
          />
          {mutation.isError ? (
            <p role="alert" className="text-destructive">
              {mutation.errorMessage(mutation.error)}
            </p>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={mutation.isPending}
              onClick={close}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={!name.trim() || mutation.isPending}
            >
              {mutation.isPending ? pendingLabel : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
